import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  getSession,
  logoutUser,
} from "../services/api";
import { clearAdminDataCache } from "../utils/adminDataCache";

export const AuthContext = createContext();

const AUTHENTICATED_SESSION = "cookie-session";

export const AuthProvider = ({ children }) => {
  const [token, setTokenState] = useState(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  const setToken = useCallback((value) => {
    setTokenState(value ? AUTHENTICATED_SESSION : null);
  }, []);

  const refreshSession = useCallback(async () => {
    setAuthLoading(true);

    try {
      const response = await getSession();

      if (response.data?.authenticated) {
        setTokenState(AUTHENTICATED_SESSION);
        setUser(response.data?.user || null);
        setSessionChecked(true);
        return true;
      }
    } catch {
      /* A missing/expired admin cookie simply means signed out. */
    } finally {
      setAuthLoading(false);
    }

    setTokenState(null);
    setUser(null);
    setSessionChecked(true);
    return false;
  }, []);

  useEffect(() => {
    /* V4 migration: remove any JWT left by older NexRojgar builds. */
    localStorage.removeItem("token");

    /*
     * Do not call /auth/session on every public page load. Public visitors and
     * Google renderers do not need an admin session check. Login/ProtectedRoute
     * request it lazily only when authentication is actually relevant.
     */
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      setTokenState(null);
      setUser(null);
      setSessionChecked(true);
      localStorage.removeItem("token");
      clearAdminDataCache();
    };

    window.addEventListener("nexrojgar:auth-expired", handleAuthExpired);

    return () => {
      window.removeEventListener("nexrojgar:auth-expired", handleAuthExpired);
    };
  }, []);

  const logout = useCallback(async () => {
    setTokenState(null);
    setUser(null);
    setSessionChecked(true);
    localStorage.removeItem("token");
    clearAdminDataCache();

    try {
      await logoutUser();
    } catch {
      /* Local state is already cleared even if the network is unavailable. */
    }
  }, []);

  const value = useMemo(
    () => ({
      token,
      setToken,
      user,
      setUser,
      authLoading,
      sessionChecked,
      refreshSession,
      logout,
    }),
    [
      token,
      setToken,
      user,
      authLoading,
      sessionChecked,
      refreshSession,
      logout,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
